import { ApprovalMode } from "./approvalModes";
import { withAuthoringService } from "./boardAuthoringDeps";
import {
  type ClientToolExecutionResult,
  createClientTool,
  toolError,
  ToolErrorKind,
  toolSuccess,
} from "./ClientTools";
import {
  draftResult,
  EDITOR_OPENING_ID_PROPERTY,
  invalidEditorOpeningIdError,
  isEditorOpeningId,
  noEditorError,
  otherEditorError,
  STAGE_CHANGEABILITY_NOTE,
} from "./panelDraftToolShared";
import { OPERATION_NAMES, toUpdateOperations } from "./updateBoardInput";

export interface UpdatePanelDraftInput {
  editor_opening_id?: unknown;
  version?: unknown;
  operations?: unknown;
}

async function execute(
  input: UpdatePanelDraftInput,
): Promise<ClientToolExecutionResult> {
  const editorOpeningId = input.editor_opening_id;
  if (!isEditorOpeningId(editorOpeningId)) {
    return invalidEditorOpeningIdError();
  }
  const version = input.version;
  if (typeof version !== "string" || version === "") {
    return toolError(
      "version must be the version the last client_read_panel_draft or client_update_panel_draft returned.",
      {},
      ToolErrorKind.VALIDATION,
    );
  }
  const parsed = toUpdateOperations(input.operations);
  if ("problem" in parsed) {
    return toolError(parsed.problem, {}, ToolErrorKind.VALIDATION);
  }
  if (parsed.operations.length === 0) {
    return toolError(
      "operations is empty, so there is nothing to change. Send at least one operation.",
      {},
      ToolErrorKind.VALIDATION,
    );
  }
  return withAuthoringService(async (service) => {
    const outcome = await service.updatePanelDraft({
      editorOpeningId,
      version,
      operations: parsed.operations,
    });
    switch (outcome.status) {
      case "no-editor": {
        return noEditorError(editorOpeningId, "change");
      }
      case "other-editor": {
        return otherEditorError(editorOpeningId, outcome.boardId);
      }
      case "unknown-version": {
        return toolError(
          `Version "${outcome.version}" is not one this editor gave out, or is too old to compare against. Nothing was changed. Read the draft with client_read_panel_draft and write the operations against what it returns.`,
          { version: outcome.version },
          ToolErrorKind.VALIDATION,
        );
      }
      case "conflict": {
        return toolError(
          `Since version "${version}" was handed out, ${outcome.changed.join(", ")} changed in the editor, so this call applied nothing. Read the draft again and write the operations against what it returns.`,
          { changed: outcome.changed },
          ToolErrorKind.CONFLICT,
        );
      }
      case "busy": {
        return toolError(
          "The panel editor was still applying an earlier change, so this call applied nothing. Read the draft again with client_read_panel_draft and retry against what it returns.",
          {},
          ToolErrorKind.CONFLICT,
        );
      }
      case "refused": {
        return toolError(
          "Nothing was changed. `reasons` lists why, one entry per " +
            "problem; resolve every one before calling again.",
          { reasons: outcome.reasons },
          ToolErrorKind.VALIDATION,
        );
      }
      case "applied": {
        return toolSuccess("updated_panel_draft", {
          summary: outcome.summary,
          changed_stage_ids: outcome.changedStageIds,
          ...draftResult(outcome.draft),
          note:
            "The editor shows this change as an unsaved edit the user can " +
            "undo; nothing is saved until the user saves. The changed " +
            "stages run again in the editor, so their run_status and " +
            "columns may not reflect the change yet; read the draft again " +
            "for their results. issues lists what still needs fixing. " +
            STAGE_CHANGEABILITY_NOTE,
        });
      }
    }
  });
}

/**
 * `client_update_panel_draft`: panel and stage operations applied to the
 * unsaved draft of the panel editor open in this tab. The editor shows the
 * change at once as an edit the user can undo, and nothing is saved, so the
 * tool auto-executes by default. The operations are `client_update_board`'s,
 * read by the same parser and limited to the draft's own panel and the
 * stages only the draft reads.
 */
export const tool = createClientTool<UpdatePanelDraftInput>({
  name: "client_update_panel_draft",
  description:
    "Changes the unsaved draft in the panel editor open in this tab and " +
    "saves nothing: the editor shows the change at once, the user can undo " +
    "it, and the user decides whether to save. Read the draft first with " +
    "client_read_panel_draft. Takes client_update_board's operations, " +
    "limited to the draft: add with the draft's id gives a draft with no " +
    "panel kind its panel; replace and set_participation_and_publish change a draft " +
    "that has one; add_stage adds a stage at the end and must list the " +
    "stages it reads in inputs; replace_stage, rename_stage and " +
    "remove_stage change only stages whose changeability is 'changeable'. A stage left " +
    "invalid is applied and reported in issues, not refused, so a broken " +
    "stage can be fixed over several calls. Returns the summary the editor " +
    "shows, the draft after the change, and a new version. The call " +
    "conflicts only when a panel or stage the operations name has changed " +
    "since the version it passes.",
  input_schema: {
    type: "object",
    required: ["editor_opening_id", "version", "operations"],
    properties: {
      editor_opening_id: EDITOR_OPENING_ID_PROPERTY,
      version: {
        type: "string",
        description:
          "The version from the read or update the operations were written from.",
      },
      operations: {
        type: "array",
        description:
          "The panel and stage edits, applied together. A panel id and a " +
          "stage id may each appear in one operation only.",
        items: {
          type: "object",
          required: ["op"],
          properties: {
            op: {
              type: "string",
              // The draft refuses to remove its own panel.
              enum: OPERATION_NAMES.filter((name) => name !== "remove"),
              description:
                "Send only the members the operation uses. Panel: add and " +
                "replace (panel_id, panel); set_participation_and_publish (panel_id, " +
                "and participation or publish or both). Stages: add_stage " +
                "(stage_id, stage); replace_stage (stage_id, stage); " +
                "rename_stage (stage_id, name); remove_stage (stage_id).",
            },
            panel_id: {
              type: "string",
              description:
                "The draft's id: draft_id in client_read_panel_draft's " +
                "result and in the board context's editing.",
            },
            panel: {
              type: "object",
              description:
                "The whole panel, as one item of client_create_board's " +
                "panels, with its id equal to panel_id.",
            },
            stage_id: {
              type: "string",
              description:
                "The id of the stage the operation is on; for add_stage, " +
                "an id no stage on the board has.",
            },
            stage: {
              type: "object",
              description:
                "The whole stage {id, name, inputs?, body}, as one item of " +
                "client_create_board's stages, with id equal to stage_id. " +
                "A stage that reads other stages lists them in inputs.",
            },
            name: {
              type: "string",
              description:
                "The stage's name after the rename. Its id does not change.",
            },
            participation: {
              description:
                "The panel's participation member as client_create_board " +
                "declares it; null removes it.",
            },
            publish: {
              description:
                "The panel's publish member as client_create_board " +
                "declares it; null removes it.",
            },
          },
        },
      },
    },
  },
  execute,
  defaultApprovalMode: ApprovalMode.AUTO_EXECUTE,
  allowableApprovalModes: [
    ApprovalMode.REQUIRE_APPROVAL,
    ApprovalMode.FOLLOW_AUTO,
    ApprovalMode.AUTO_EXECUTE,
  ],
  displayName: "Change panel draft",
  displayDescription:
    "Changes the unsaved panel open in the panel editor. The user can undo the change, and nothing is saved until they save.",
});
